import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, NavLink, useParams } from 'react-router-dom';
//import ReactTooltip from 'react-tooltip';

import PropTypes from 'prop-types';

import disabledImage from '@/components/common/icons/clients-disabled.svg';
import enabledImage from '@/components/common/icons/clients-enabled.svg';
// import devicesIcon from '@/components/common/icons/devices.svg';
import ganttIcon from '@/components/common/icons/gantt.svg';
import plusCircleImage from '@/components/common/icons/plus-circle.svg';
import usersIcon from '@/components/common/icons/users.svg';
import { orderType as parseOrderType } from '@/constants';
import getDashboardLink from '@/helpers/get-dashboard-link';
import getDivergencesPanelLink from '@/helpers/get-divergences-panel-link';
import CalendarIcon from '@/svg-icons/calendar-menu';
import DivergencesIcon from '@/svg-icons/divergences-menu';
import InspectorsIcon from '@/svg-icons/inspectors';
import ClientIcon from '@/svg-icons/menu-clientes';
import MenuDashboard from '@/svg-icons/menu-dashboard';
import SettingsIcon from '@/svg-icons/menu-settings';
import InspectionIcon from '@/svg-icons/menu-vistorias';

const ArrowheadLeft = () => (
  <svg fill="#ffffff" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <g data-name="Layer 2">
      <g data-name="arrowhead-left">
        <path d="M11.64 5.23a1 1 0 0 0-1.41.13l-5 6a1 1 0 0 0 0 1.27l4.83 6a1 1 0 0 0 .78.37 1 1 0 0 0 .78-1.63L7.29 12l4.48-5.37a1 1 0 0 0-.13-1.4z" />
        <path d="M14.29 12l4.48-5.37a1 1 0 0 0-1.54-1.28l-5 6a1 1 0 0 0 0 1.27l4.83 6a1 1 0 0 0 .78.37 1 1 0 0 0 .78-1.63z" />
      </g>
    </g>
  </svg>
);

const ArrowheadRight = () => (
  <svg fill="#ffffff" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <g data-name="Layer 2">
      <g data-name="arrowhead-right">
        <path d="M12.36 18.77a1 1 0 0 0 1.41-.13l5-6a1 1 0 0 0 0-1.27l-4.83-6a1 1 0 0 0-.78-.37 1 1 0 0 0-.78 1.63l4.35 5.37-4.48 5.37a1 1 0 0 0 .13 1.4z" />
        <path d="M9.71 12l-4.48 5.37a1 1 0 0 0 1.54 1.28l5-6a1 1 0 0 0 0-1.27l-4.83-6a1 1 0 0 0-.78-.37 1 1 0 0 0-.78 1.63z" />
      </g>
    </g>
  </svg>
);

const SideBar = ({ handleToggleSidebar, sidebarCollapsed }) => {
  const divergenceCount = useSelector(state => state?.divergences?.count);
  const features = JSON.parse(window.sessionStorage.getItem('me'))?.features.map(feature =>
    parseOrderType.get(feature)
  );
  const params = useParams();
  const intl = useIntl();

  const { page } = params;

  const menu = useMemo(
    () => ({
      inspectors: {
        icon: <InspectorsIcon />,
        title: intl.formatMessage({ id: 'INSPECTORS' }),
        link: '/inspectors',
        requiredFeature: 'inspector',
        actions: [
          {
            link: '/inspectors/new',
            icon: plusCircleImage,
            title: intl.formatMessage({ id: 'ADD_INSPECTOR' }),
          },
        ],
        children: [
          {
            link: '/inspectors/active',
            icon: enabledImage,
            title: intl.formatMessage({ id: 'ENABLED' }),
          },
          {
            link: '/inspectors/deleted',
            icon: disabledImage,
            title: intl.formatMessage({ id: 'DISABLED' }),
          },
        ],
      },
      franchisee: {
        icon: <InspectionIcon />,
        title: intl.formatMessage({ id: 'INSPECTIONS' }),
        link: '/orders',
        children: [],
      },
      schedule: {
        icon: <CalendarIcon />,
        title: intl.formatMessage({ id: 'SCHEDULE' }),
        link: '/schedule',
        requiredFeature: 'schedule',
        children: [
          {
            link: '/schedule',
            icon: ganttIcon,
            title: intl.formatMessage({ id: 'OVERVIEW' }),
          },
        ],
      },
      clients: {
        icon: <ClientIcon />,
        title: intl.formatMessage({ id: 'CUSTOMERS' }),
        link: '/clients',
        requiredFeature: 'client',
        actions: [
          {
            link: '/clients/new',
            icon: plusCircleImage,
            title: intl.formatMessage({ id: 'ADD_CUSTOMER' }),
          },
        ],
        children: [
          {
            link: '/clients/enabled',
            icon: enabledImage,
            title: intl.formatMessage({ id: 'ENABLED' }),
          },
          {
            link: '/clients/disabled',
            icon: disabledImage,
            title: intl.formatMessage({ id: 'DISABLED' }),
          },
        ],
      },
      divergences: {
        icon: <DivergencesIcon />,
        title: intl.formatMessage({ id: 'DIVERGENCES' }),
        link: null,
        onClick: getDivergencesPanelLink,
        requiredFeature: 'divergencePanel',
      },
      dashboard: {
        icon: <MenuDashboard />,
        title: intl.formatMessage({ id: 'DASHBOARD' }),
        link: null,
        onClick: getDashboardLink,
      },
      settings: {
        icon: <SettingsIcon />,
        title: intl.formatMessage({ id: 'SETTINGS' }),
        link: '/settings',
        requiredFeature: 'user',
        actions: [
          {
            link: '/settings/users/new',
            icon: plusCircleImage,
            title: intl.formatMessage({ id: 'ADD_USER' }),
          },
        ],
        children: [
          {
            link: '/settings/users',
            icon: usersIcon,
            title: intl.formatMessage({ id: 'USERS' }),
          },
        ],
      },
    }),
    [intl]
  );

  const activeMenu = useMemo(() => menu[page] || null, [menu, page]);

  return (
    <aside
      className={`relative transition-all duration-300 flex flex-col flex-shrink-0 min-h-[330px] pt-[24.8px] pb-[18.4px] bg-[#1a3a5f] text-white text-[14px] z-10 font-['Open_Sans'] ${
        sidebarCollapsed
          ? 'w-[70px] min-w-[70px] max-w-[70px] px-0'
          : 'w-[220px] min-w-[220px] max-w-[220px] px-[18.4px]'
      }`}
    >
      <button
        className="absolute top-1/2 right-[-10px] translate-y-[-50%] w-[35px] h-[35px] aspect-square bg-[#143253] border-none !rounded-full overflow-hidden z-[100] flex items-center justify-center font-bold outline-none cursor-pointer p-0 transition-opacity hover:opacity-80"
        onClick={handleToggleSidebar}
      >
        {sidebarCollapsed ? <ArrowheadRight /> : <ArrowheadLeft />}
      </button>

      <h3
        className={`flex items-center font-bold text-[15.4px] transition-colors duration-300 mb-2 leading-none ${
          sidebarCollapsed ? 'text-transparent justify-center' : 'text-[#5578a0]'
        }`}
      >
        <span
          className={`flex justify-center items-center rounded-full ${
            sidebarCollapsed ? 'mr-0' : 'mr-[8px]'
          }`}
          title={sidebarCollapsed ? activeMenu?.title : ''}
        >
          {activeMenu && React.cloneElement(activeMenu.icon, { className: 'text-[34px] w-[34px] h-[34px]' })}
        </span>
        <span className="text-[15.4px] font-bold text-[#5578a0]">{!sidebarCollapsed && activeMenu?.title}</span>
        {!sidebarCollapsed && activeMenu?.actions && (
          <ul className="flex items-center justify-end list-none ml-auto mb-0 p-0">
            {activeMenu.actions.map(action => {
              if (action.requiredFeature && features?.indexOf(action.requiredFeature) < 0) return null;
              return (
                <li className="ml-[8px]" key={action.link} data-tip={action.title}>
                  <Link
                    to={action.link}
                    className="flex items-center justify-center w-[24px] h-[24px] transition-opacity hover:opacity-75 focus:outline-0"
                  >
                    <img src={action.icon} alt={action.title} className="max-w-full max-h-full" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </h3>

      <ul className={`list-none m-0 p-0 ${sidebarCollapsed ? 'hidden' : 'block'}`}>
        {activeMenu &&
          activeMenu.children.map(child => (
            <li key={child.link} className="sidebar-primary-item">
              <NavLink
                to={child.link}
                className={({ isActive }) =>
                  `sidebar-primary-link ${isActive ? 'active' : ''}`
                }
              >
                <span className="w-[28px] h-[28px] mr-[10.5px] leading-[0] flex items-center justify-center shrink-0">
                  {typeof child.icon === 'string' ? (
                    <img src={child.icon} alt={child.title} className="w-full h-full" />
                  ) : (
                    React.cloneElement(child.icon, { className: 'text-[28px] w-[28px] h-[28px]' })
                  )}
                </span>
                {child.title}
              </NavLink>
            </li>
          ))}
      </ul>

      <ul className={`sidebar-secondary-menu ${sidebarCollapsed ? 'collapsed' : 'expanded'}`}>
        {Object.keys(menu).map(key => {
          if (key === page) return null;
          if (menu[key].requiredFeature && features?.indexOf(menu[key].requiredFeature) < 0) return null;

          return (
            <li
              key={key}
              title={sidebarCollapsed ? menu[key]?.title : ''}
              className="sidebar-secondary-item"
            >
              {key === 'divergences' && !!divergenceCount && (
                <span className={`sidebar-divergence-badge ${sidebarCollapsed ? 'collapsed' : 'expanded'}`}>
                  {divergenceCount}
                </span>
              )}
              {menu[key].link ? (
                <NavLink
                  to={menu[key].link}
                  className={({ isActive }) =>
                    `sidebar-secondary-link ${sidebarCollapsed ? 'collapsed' : 'expanded'} ${
                      isActive ? 'active' : ''
                    }`
                  }
                >
                  <span className={`sidebar-secondary-icon-wrapper ${sidebarCollapsed ? 'collapsed' : 'expanded'}`}>
                    {React.cloneElement(menu[key].icon, { className: 'text-[34px] w-[34px] h-[34px]' })}
                  </span>
                  {!sidebarCollapsed && menu[key].title}
                </NavLink>
              ) : (
                <a
                  href="/"
                  className={`sidebar-secondary-link ${sidebarCollapsed ? 'collapsed' : 'expanded'}`}
                  onClick={e => {
                    e.preventDefault();
                    if (menu[key]?.onClick && typeof menu[key]?.onClick === 'function') {
                      menu[key].onClick();
                    }
                  }}
                >
                  <span className={`sidebar-secondary-icon-wrapper ${sidebarCollapsed ? 'collapsed' : 'expanded'}`}>
                    {React.cloneElement(menu[key].icon, { className: 'text-[34px] w-[34px] h-[34px]' })}
                  </span>
                  {!sidebarCollapsed && menu[key].title}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

SideBar.propTypes = {
  handleToggleSidebar: PropTypes.func.isRequired,
  sidebarCollapsed: PropTypes.bool.isRequired,
};

export default SideBar;
